import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { equipmentAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const EditEquipmentDialog = ({ equipment, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    quantity: '',
    available: '',
    price: '',
    serialNumber: '',
    status: 'available',
    location: '',
    condition: 'good'
  });

  useEffect(() => {
    if (equipment) {
      setFormData({
        name: equipment.name || '',
        category: equipment.category || '',
        subcategory: equipment.subcategory || '',
        quantity: equipment.quantity?.toString() || '',
        available: equipment.available?.toString() || '',
        price: equipment.price?.toString() || '',
        serialNumber: equipment.serialNumber || '',
        status: equipment.status || 'available',
        location: equipment.location || '',
        condition: equipment.condition || 'good'
      });
    }
  }, [equipment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await equipmentAPI.update(equipment.id, {
        ...formData,
        quantity: parseInt(formData.quantity),
        available: parseInt(formData.available),
        price: parseFloat(formData.price)
      });
      
      toast({
        title: "Gelukt",
        description: "Apparatuur succesvol bijgewerkt"
      });
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan apparatuur niet bijwerken",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apparatuur Bewerken</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-name">Apparatuur Naam *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-category">Hoofdcategorie *</Label>
              <Input
                id="edit-category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="bijv. Geluid, Licht, Video"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-subcategory">Subcategorie</Label>
              <Input
                id="edit-subcategory"
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                placeholder="bijv. Speakers, Moving Heads"
              />
            </div>
            <div>
              <Label htmlFor="edit-quantity">Totale Hoeveelheid *</Label>
              <Input
                id="edit-quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-available">Beschikbare Hoeveelheid *</Label>
              <Input
                id="edit-available"
                type="number"
                value={formData.available}
                onChange={(e) => setFormData({ ...formData, available: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-price">Prijs per Dag (€) *</Label>
              <Input
                id="edit-price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-serialNumber">Serienummer *</Label>
              <Input
                id="edit-serialNumber"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-location">Locatie *</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="bijv. Magazijn A"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Beschikbaar</SelectItem>
                  <SelectItem value="rented">Verhuurd</SelectItem>
                  <SelectItem value="maintenance">Onderhoud</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-condition">Conditie</Label>
              <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Uitstekend</SelectItem>
                  <SelectItem value="good">Goed</SelectItem>
                  <SelectItem value="fair">Redelijk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-600 to-purple-600">
              {loading ? 'Opslaan...' : 'Wijzigingen Opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditEquipmentDialog;
